import { authTokenStore } from "./authTokenStore";

const createRequest = (input: RequestInfo, init: RequestInit): Request => {
  const inputHeaders =
    typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined;
  const headers = new Headers(inputHeaders);

  new Headers(init.headers).forEach((value, key) => {
    headers.set(key, value);
  });

  return new Request(input, {
    ...init,
    headers,
  });
};

const addAuthorization = (request: Request, token: string): Request => {
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);

  return new Request(request, { headers });
};

export async function authenticatedFetch(
  input: RequestInfo,
  init: RequestInit = {},
): Promise<Response> {
  const token = authTokenStore.get();

  const baseRequest = createRequest(input, init);

  const url = new URL(baseRequest.url, window.location.origin);

  const isInternalRequest = url.origin === window.location.origin;

  const suppliedAuthorization = baseRequest.headers.get("Authorization");

  const usesCurrentToken = suppliedAuthorization === `Bearer ${token}`;

  const shouldManageAuthorization =
    Boolean(token) && isInternalRequest && (!suppliedAuthorization || usesCurrentToken);

  const retryRequest = shouldManageAuthorization ? baseRequest.clone() : null;

  const initialRequest = shouldManageAuthorization
    ? addAuthorization(baseRequest, token)
    : baseRequest;

  const response = await fetch(initialRequest);

  if (response.status !== 401 || !retryRequest) return response;

  try {
    const refreshedToken = await authTokenStore.refresh();

    if (!refreshedToken) return response;

    return fetch(addAuthorization(retryRequest, refreshedToken));
  } catch {
    return response;
  }
}

export async function fetchJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const response = await authenticatedFetch(input, init);

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
